// User-configurable notification behaviour shared between the page and the service worker.
// The auto-dismiss preference lives in localStorage (see platform/store.ts), but the SW
// can't read localStorage — so the page mirrors this one value into Cache Storage, which
// both contexts can read. The SW consults it on each push to decide how long a non-critical
// OS notification stays before it self-closes (catalog §5). Critical alerts always persist.

const CONFIG_CACHE = "beacon-config-v1";
const CONFIG_URL = "/__beacon/notif-config.json";

/** Default auto-dismiss delay for non-critical OS notifications (single source of truth). */
export const DEFAULT_AUTO_DISMISS_MS = 12_000;

/** Selectable auto-dismiss delays for the You-tab control. 0 = stay until the user acts. */
export const AUTO_DISMISS_OPTIONS = [5_000, DEFAULT_AUTO_DISMISS_MS, 30_000, 0] as const;

export interface NotifConfig {
  autoDismissMs: number; // 0 = never auto-dismiss (notification stays until the user acts)
}

function defaultCaches(): CacheStorage | undefined {
  return typeof caches !== "undefined" ? caches : undefined;
}

/** Mirror the current notification config into Cache Storage (page side). No-op without caches. */
export async function writeNotifConfig(cfg: NotifConfig, store = defaultCaches()): Promise<void> {
  if (!store) return;
  try {
    const cache = await store.open(CONFIG_CACHE);
    await cache.put(
      CONFIG_URL,
      new Response(JSON.stringify(cfg), { headers: { "content-type": "application/json" } }),
    );
  } catch {
    /* best-effort: a failed mirror just falls back to the default delay */
  }
}

/** Read the mirrored notification config (SW side). Returns null when unset or unreadable. */
export async function readNotifConfig(store = defaultCaches()): Promise<NotifConfig | null> {
  if (!store) return null;
  try {
    const cache = await store.open(CONFIG_CACHE);
    const res = await cache.match(CONFIG_URL);
    if (!res) return null;
    const j = (await res.json()) as Partial<NotifConfig>;
    if (typeof j.autoDismissMs !== "number" || !Number.isFinite(j.autoDismissMs) || j.autoDismissMs < 0) {
      return null;
    }
    return { autoDismissMs: j.autoDismissMs };
  } catch {
    return null;
  }
}
